import { create } from 'zustand'
import type { PendingAttachment } from '@/lib/attachments'

export interface QueuedMessage {
  id: string
  conversationId: string
  text: string
  attachments: PendingAttachment[]
  createdAt: number
}

export interface ConversationQueue {
  items: QueuedMessage[]
  held: boolean
  editingId: string | null
}

interface MessageQueueState {
  queues: Record<string, ConversationQueue>
  enqueue: (
    conversationId: string,
    text: string,
    attachments?: PendingAttachment[]
  ) => QueuedMessage
  edit: (conversationId: string, id: string, text: string) => void
  setEditing: (conversationId: string, id: string | null) => void
  reorder: (conversationId: string, id: string, toIndex: number) => void
  remove: (conversationId: string, id: string) => void
  hold: (conversationId: string) => void
  resume: (conversationId: string) => void
  dequeueHead: (conversationId: string) => QueuedMessage | undefined
  requeue: (conversationId: string, item: QueuedMessage) => void
  clear: (conversationId: string) => void
}

export const EMPTY_QUEUE: ConversationQueue = {
  items: [],
  held: false,
  editingId: null,
}

// An empty queue is never held: nothing to resume, so the flag would only
// block the next item a user adds.
function normalize(queue: ConversationQueue): ConversationQueue {
  return queue.items.length ? queue : EMPTY_QUEUE
}

// Reload loses the queue (in memory only); persist text-only items if users ask for it.
export const useMessageQueue = create<MessageQueueState>()((set, get) => {
  // Returns the same state when nothing changed, so a repeated hold or
  // resume does not wake subscribers.
  const update = (
    conversationId: string,
    fn: (queue: ConversationQueue) => ConversationQueue
  ) =>
    set((s) => {
      const prev = s.queues[conversationId] ?? EMPTY_QUEUE
      const next = normalize(fn(prev))
      if (next === prev) return s
      return { queues: { ...s.queues, [conversationId]: next } }
    })

  return {
    queues: {},

    enqueue: (conversationId, text, attachments = []) => {
      const item: QueuedMessage = {
        id: crypto.randomUUID(),
        conversationId,
        text,
        attachments,
        createdAt: Date.now(),
      }
      update(conversationId, (q) => ({ ...q, items: [...q.items, item] }))
      return item
    },

    edit: (conversationId, id, text) =>
      update(conversationId, (q) => ({
        ...q,
        editingId: q.editingId === id ? null : q.editingId,
        items: q.items.map((i) => (i.id === id ? { ...i, text } : i)),
      })),

    setEditing: (conversationId, id) =>
      update(conversationId, (q) => ({ ...q, editingId: id })),

    reorder: (conversationId, id, toIndex) =>
      update(conversationId, (q) => {
        const item = q.items.find((i) => i.id === id)
        if (!item) return q
        const items = q.items.filter((i) => i.id !== id)
        items.splice(Math.max(0, Math.min(toIndex, items.length)), 0, item)
        return { ...q, items }
      }),

    remove: (conversationId, id) =>
      update(conversationId, (q) => ({
        ...q,
        editingId: q.editingId === id ? null : q.editingId,
        items: q.items.filter((i) => i.id !== id),
      })),

    hold: (conversationId) =>
      update(conversationId, (q) => (q.held ? q : { ...q, held: true })),

    resume: (conversationId) =>
      update(conversationId, (q) => (q.held ? { ...q, held: false } : q)),

    dequeueHead: (conversationId) => {
      const queue = get().queues[conversationId]
      if (!queue || queue.held) return undefined
      const [head, ...rest] = queue.items
      if (!head) return undefined
      update(conversationId, (q) => ({
        ...q,
        editingId: q.editingId === head.id ? null : q.editingId,
        items: rest,
      }))
      return head
    },

    requeue: (conversationId, item) =>
      update(conversationId, (q) => ({ ...q, items: [item, ...q.items] })),

    clear: (conversationId) =>
      set((s) => {
        if (!(conversationId in s.queues)) return s
        const { [conversationId]: _, ...queues } = s.queues
        return { queues }
      }),
  }
})
